class PdpReviewHighlights extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const section = this.closest('.product-information');
    const trackId = this.querySelector('.pdp-review-highlights__track')?.id;
    const placeReviews = () => {
      if (!this.isConnected || !section) return;
      // A variant change swaps the media gallery for the server's copy, which
      // carries a fresh instance of this block. The previous instance had
      // already been moved out of the gallery into the footnote, so the swap
      // leaves it behind and every flavor change would stack another copy.
      for (const stale of section.querySelectorAll('pdp-review-highlights')) {
        if (stale === this) continue;
        if (!trackId || stale.querySelector('.pdp-review-highlights__track')?.id === trackId) stale.remove();
      }
      const home = section.querySelector('[data-review-highlights-home]');
      const footnote = section.querySelector('.pdp-buy__footnote');
      // The reviews sit under the add-to-cart button at every width; the
      // gallery slot is only the fallback for a section without a buy box.
      if (footnote) {
        if (this.parentElement !== footnote) footnote.append(this);
      } else if (home && this.previousElementSibling !== home) {
        home.after(this);
      }
    };
    queueMicrotask(placeReviews);

    const track = this.querySelector('.pdp-review-highlights__track');
    if (!track) return;

    const cards = [...track.children];
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const isRtl = () => getComputedStyle(track).direction === 'rtl';

    /** Scroll offsets of every card, clamped to the track's scrollable range */
    const cardOffsets = () => {
      const position = Math.abs(track.scrollLeft);
      const maximum = track.scrollWidth - track.clientWidth;
      const trackBounds = track.getBoundingClientRect();
      const rtl = isRtl();
      // Cards snap to the track's start padding, not its edge
      const inset = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      return cards.map((card) => {
        const bounds = card.getBoundingClientRect();
        const distance = rtl ? trackBounds.right - bounds.right : bounds.left - trackBounds.left;
        return Math.max(0, Math.min(maximum, position + distance - inset));
      });
    };

    // The dots follow whichever card the track has settled nearest to
    const dots = [...this.querySelectorAll('.pdp-review-highlights__dot')];
    const updateDots = () => {
      if (!dots.length) return;
      const position = Math.abs(track.scrollLeft);
      const offsets = cardOffsets();
      let active = 0;
      for (const [index, offset] of offsets.entries()) {
        if (Math.abs(offset - position) < Math.abs(offsets[active] - position)) active = index;
      }
      for (const [index, dot] of dots.entries()) dot.classList.toggle('is-active', index === active);
    };
    track.addEventListener('scroll', updateDots, { passive: true, signal });

    const scrollTo = (destination) => {
      track.scrollTo({
        left: isRtl() ? -destination : destination,
        behavior: reducedMotion.matches ? 'instant' : 'smooth',
      });
    };

    const move = (direction) => {
      const position = Math.abs(track.scrollLeft);
      const maximum = track.scrollWidth - track.clientWidth;
      const offsets = cardOffsets();
      const destination = direction > 0
        ? offsets.find((offset) => offset > position + 2) ?? 0
        : offsets.findLast((offset) => offset < position - 2) ?? maximum;
      scrollTo(destination);
    };

    // The arrows page through the cards the same way the arrow keys do,
    // wrapping around at either end
    this.addEventListener('click', (event) => {
      const arrow = event.target instanceof Element ? event.target.closest('[data-review-arrow]') : null;
      if (arrow instanceof HTMLElement) move(Number(arrow.dataset.reviewArrow));
    }, { signal });

    let drag;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || cards.length < 2) return;
      if (event.target.closest('a, button, input')) return;
      drag = { x: event.clientX, scroll: track.scrollLeft };
      track.setPointerCapture(event.pointerId);
      event.preventDefault();
      track.focus({ preventScroll: true });
    }, { signal });

    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const distance = event.clientX - drag.x;
      if (Math.abs(distance) < 4 && !track.classList.contains('is-dragging')) return;
      track.classList.add('is-dragging');
      track.scrollLeft = drag.scroll - distance;
    }, { signal });

    const stopDragging = () => {
      if (!drag) return;
      drag = undefined;
      track.classList.remove('is-dragging');
      // Snap back onto the nearest card once the mouse lets go
      const position = Math.abs(track.scrollLeft);
      const offsets = cardOffsets();
      const nearest = offsets.reduce((best, offset) => (Math.abs(offset - position) < Math.abs(best - position) ? offset : best), offsets[0] ?? 0);
      scrollTo(nearest);
    };
    track.addEventListener('lostpointercapture', stopDragging, { signal });
    track.addEventListener('pointerup', stopDragging, { signal });
    track.addEventListener('pointercancel', stopDragging, { signal });

    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        move((event.key === 'ArrowRight') !== isRtl() ? 1 : -1);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }
}

if (!customElements.get('pdp-review-highlights')) {
  customElements.define('pdp-review-highlights', PdpReviewHighlights);
}
